/**
 * CR305: Deal Card Render Signature Strip
 * 
 * Debug component that displays the render signature JSON for a deal card.
 * Only renders when ?debugCardSig=1 is in the URL.
 * 
 * Use this to verify that all preview surfaces (Layout Editor, Deal Cards UI,
 * Theme Labs) are using the same resolver, data source, and config.
 */

import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { 
  isDebugCardSigEnabled, 
  type DealCardRenderSignature,
  type DealCardScreen,
} from "@/lib/deals/debugDealCardSignature";

interface DealCardRenderSignatureStripProps {
  sig: DealCardRenderSignature;
  className?: string;
}

// Screen-specific badge colors for visual differentiation
const SCREEN_COLORS: Record<DealCardScreen, string> = {
  layout_editor: "bg-blue-600",
  deal_cards_ui: "bg-purple-600",
  theme_labs: "bg-amber-600",
  unknown: "bg-gray-500",
};

export function DealCardRenderSignatureStrip({ 
  sig, 
  className = "" 
}: DealCardRenderSignatureStripProps) {
  // Only render if debug flag is enabled
  const isEnabled = useMemo(() => isDebugCardSigEnabled(), []);
  
  if (!isEnabled) {
    return null;
  }
  
  const screenColor = SCREEN_COLORS[sig.screen];
  
  // Key fields to highlight for quick scanning
  const keyFields = [
    { label: "screen", value: sig.screen },
    { label: "resolver", value: sig.config.resolver },
    { label: "source", value: sig.deal.source },
    { label: "preset", value: sig.layout.active_preset_id?.slice(0, 8) || "none" },
    { label: "tokens", value: sig.theme.tokens_source },
    { label: "helpers", value: sig.config.uses_deal_card_display_helpers ? "✓" : "✗" },
  ];

  return (
    <div className={`mt-3 border rounded-lg overflow-hidden text-xs ${className}`}>
      {/* Header with screen identifier */}
      <div className={`${screenColor} text-white px-3 py-1.5 flex items-center justify-between`}>
        <span className="font-semibold">
          Deal Card Render Signature
        </span>
        <Badge variant="outline" className="bg-white/20 text-white border-white/40 text-[10px]">
          {sig.screen}
        </Badge>
      </div>
      
      {/* Quick summary row */}
      <div className="bg-muted/50 px-3 py-1.5 flex flex-wrap gap-2 border-b">
        {keyFields.map(({ label, value }) => (
          <span key={label} className="text-[10px]">
            <span className="text-muted-foreground">{label}=</span>
            <span className="font-mono text-foreground">{value}</span>
          </span>
        ))}
      </div>
      
      {/* Full JSON output */}
      <div className="p-2 bg-slate-900 text-slate-100 overflow-x-auto">
        <pre className="text-[10px] leading-relaxed whitespace-pre-wrap">
          {JSON.stringify(sig, null, 2)}
        </pre>
      </div>
    </div>
  );
}
